import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "financial-container w-full" }
const _hoisted_2 = { class: "es-financial-group row gap-06 w-full" }
const _hoisted_3 = { class: "font-m" }
const _hoisted_4 = {
  key: 0,
  class: "txt-grey-40"
}
const _hoisted_5 = { key: 1 }

import { ApiError, FinancialGroupLightResponse, AddOrUpdateFinancialGroupRequest, PersonApi } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { useAppUserStore } from '@/stores/appUser';
import { toastError, toastSuccess } from '@/utils/toast-utils';
import { useToast } from 'primevue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import EsFinancialGroupDetailSettings from '@/components/settings/accounting/esFinancialGroupDetailSettings.vue';
import EsFinancialGroupForm from '@/components/form/accounting/esFinancialGroupForm.vue';
import { ItemSelect } from '@/models/share.model';
import Select from 'primevue/select';

type FinancialGroupItem = ItemSelect & {
    disabled?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'esFinancialGroupSettings',
  setup(__props) {

const personApi = new PersonApi(medServerApiConfig);

const toast = useToast();
const { t } = useI18n();
const appUserStore = useAppUserStore();
const loading = ref<boolean>(true);
const openFormModal = ref<boolean>(false);
const headerFormModal = ref<string>("")
const financialGroups = ref<FinancialGroupLightResponse[]>([]);
const financialGroupsItemSelect = ref<FinancialGroupItem[]>([]);
const selectedFinancialGroup = ref<FinancialGroupItem>();
const selectedFinancialGroupOwner = ref<string>("");
const selectedFinancialGroupActive = ref<boolean>(true);

const currentPersonId = computed(() => {
  if (!appUserStore.currentAppUserData || !appUserStore.currentAppUserData.healthGroupMemberships) {return null}
  return appUserStore.currentAppUserData.healthGroupMemberships[0].pwr.personWithRoleId
});

const searchFinancialGroupByItemSelect = (itemSelect: ItemSelect): FinancialGroupLightResponse => {
    return financialGroups.value.filter((financialGroup) => financialGroup.id.toString() === itemSelect.id)[0];
}

const transformToItemSelect = (dataToTransform: FinancialGroupLightResponse): FinancialGroupItem => {
    selectedFinancialGroupOwner.value = dataToTransform.owner;
    return {
        id: dataToTransform.id.toString(),
        label: dataToTransform.title,
        disabled: !dataToTransform.active
    }
};

const getFinancialGroup = () => {
    personApi.findFinancialGroupsForPerson({personId: currentPersonId.value}).then((result: FinancialGroupLightResponse[]) => {
        financialGroups.value = result;
        financialGroupsItemSelect.value = financialGroups.value.map(financialGroup => transformToItemSelect(financialGroup));
        if (financialGroupsItemSelect.value.length > 0) {
            changeFinancialGroup(financialGroupsItemSelect.value[0]);
            selectedFinancialGroup.value = financialGroupsItemSelect.value[0];
        }
        loading.value = false;
    }).catch((error: ApiError) => {
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
        });
    });
}

const openAddForm = () => {
    openFormModal.value = true;
    selectedFinancialGroup.value = null;
    headerFormModal.value = t('settings.label.accounting.financialGroup.form.header.add');
}

const openUpdateForm = () => {
    if (selectedFinancialGroup.value) {
        openFormModal.value = true;
        headerFormModal.value = t('settings.label.accounting.financialGroup.form.header.update');
    }
}

const closeFormModal = async (addOrUpdateFinancialGroupRequest: AddOrUpdateFinancialGroupRequest) => {
    if (addOrUpdateFinancialGroupRequest && currentPersonId.value) {
        if (addOrUpdateFinancialGroupRequest.id) {
            await personApi.updateFinancialGroup({personId: currentPersonId.value, financialGroupId: addOrUpdateFinancialGroupRequest.id, addOrUpdateFinancialGroupRequest: addOrUpdateFinancialGroupRequest})
                .then(async () => {
                    toastSuccess(toast, {
                        summary: t('common.toast.success.update'),
                        detail: t('settings.label.accounting.financialGroup.message.success.update')
                    });
                    openFormModal.value = false;
                    await getFinancialGroup();
                    await changeFinancialGroup(null);
                }).catch((error: ApiError) => {
                    toastError(toast, {
                        summary: t('common.toast.error'),
                        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
                    });
                    // Throw error to set at false the var __alreadyClicked in v-one-click directive
                    throw error;
                });
        } else {
            await personApi.addFinancialGroup({personId: currentPersonId.value, addOrUpdateFinancialGroupRequest: addOrUpdateFinancialGroupRequest})
            .then(async () => {
                toastSuccess(toast, {
                    summary: t('common.toast.success.add'),
                    detail: t('settings.label.accounting.financialGroup.message.success.add')
                })
                openFormModal.value = false;
                await getFinancialGroup();
                await changeFinancialGroup(null);
            }).catch((error: ApiError) => {
                toastError(toast, {
                    summary: t('common.toast.error'),
                    detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
                });
                // Throw error to set at false the var __alreadyClicked in v-one-click directive
                throw error;
            });
        }
    }
}

const changeFinancialGroup = (setSelectedFinancialGroup: FinancialGroupItem) => {
    if (financialGroups.value && setSelectedFinancialGroup) {
        selectedFinancialGroup.value = setSelectedFinancialGroup;
        const financialGroupsFiltered = financialGroups.value.filter((financialGroup) => financialGroup.id.toString() === setSelectedFinancialGroup.id)[0];
        selectedFinancialGroupOwner.value = financialGroupsFiltered ? financialGroupsFiltered.owner : undefined;
        selectedFinancialGroupActive.value = financialGroupsFiltered.active;
    } else {
        selectedFinancialGroup.value = undefined;
        selectedFinancialGroupOwner.value = undefined;
        selectedFinancialGroupActive.value = true;
    }
}

watch(
  () => selectedFinancialGroup.value,
  () => {
    changeFinancialGroup(selectedFinancialGroup.value);
  }
)

onMounted(async () => {
    await getFinancialGroup();
});


return (_ctx: any,_cache: any) => {
  const _component_es_button_icon = _resolveComponent("es-button-icon")!
  const _component_es_button = _resolveComponent("es-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('settings.label.accounting.financialGroup.title')), 1),
      _createVNode(_unref(Select), {
        modelValue: selectedFinancialGroup.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedFinancialGroup).value = $event)),
        options: financialGroupsItemSelect.value,
        optionLabel: "label",
        size: "small",
        class: "w-full"
      }, {
        option: _withCtx((slotProps) => [
          (slotProps.option.disabled)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(t)('settings.label.accounting.financialGroup.optionDisabled', { name: slotProps.option.label })), 1))
            : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(slotProps.option.label), 1))
        ]),
        _: 1
      }, 8, ["modelValue", "options"]),
      _createVNode(_component_es_button_icon, {
        class: "mright-05 pad-m",
        icon: "pencil_fill",
        folder: "shareIcons",
        styles: "img-grey",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (openUpdateForm())),
        disabled: !selectedFinancialGroup.value || selectedFinancialGroupOwner.value !== currentPersonId.value
      }, null, 8, ["disabled"]),
      _createVNode(_component_es_button, {
        widthClass: "w-full",
        label: _unref(t)('action.add'),
        onClick: _cache[2] || (_cache[2] = ($event: any) => (openAddForm())),
        "is-primary": false,
        "is-linked": false
      }, null, 8, ["label"])
    ]),
    _createVNode(EsFinancialGroupDetailSettings, {
      selectedFinancialGroupActive: selectedFinancialGroupActive.value,
      selectedFinancialGroup: selectedFinancialGroup.value
    }, null, 8, ["selectedFinancialGroupActive", "selectedFinancialGroup"]),
    _createVNode(EsFinancialGroupForm, {
      isOpen: openFormModal.value,
      "is-owner": selectedFinancialGroupOwner.value === currentPersonId.value,
      header: headerFormModal.value,
      financialGroupLightResponse: selectedFinancialGroup.value ? searchFinancialGroupByItemSelect(selectedFinancialGroup.value) : null,
      "confirm-method": closeFormModal,
      onModalClose: _cache[3] || (_cache[3] = ($event: any) => (openFormModal.value = false))
    }, null, 8, ["isOpen", "is-owner", "header", "financialGroupLightResponse"])
  ]))
}
}

})